import type { KarmaNumerology } from "@/lib/karma/types";
import { countElements, type ElementKey } from "@/lib/karma/numerology";

export type KarmaArchetypeTone = "strength" | "growth" | "practice";

export type KarmaArchetypeId =
  | "a1"
  | "a2"
  | "a3"
  | "a4"
  | "a5"
  | "a6"
  | "a7"
  | "a8"
  | "a9";

export type KarmaArchetype = {
  id: KarmaArchetypeId;
  digit: number;
  name: string;
  headline: string;
  strengths: string;
  shadows: string;
  practice: string;
  tone: KarmaArchetypeTone;
};

export type KarmaArchetypeProfile = {
  archetype: KarmaArchetype;
  element: ElementKey;
  elementLabel: string;
  elementHint: string;
};

const ARCHETYPES: Record<number, KarmaArchetype> = {
  1: {
    id: "a1",
    digit: 1,
    name: "Öncü",
    headline: "Başlatır, yolu açar, karar alır.",
    strengths: "cesaret, liderlik, netlik",
    shadows: "acelecilik, yalnızlaşma, kontrol",
    practice: "Tek hedef seç ve bugün 10 dakikalık ilk adımı at.",
    tone: "strength",
  },
  2: {
    id: "a2",
    digit: 2,
    name: "Arabulucu",
    headline: "Birleştirir, uyum kurar, ortak dil yaratır.",
    strengths: "empati, diplomasi, sezgi",
    shadows: "kararsızlık, alınganlık, erteleme",
    practice: "Varsayım yerine tek bir net soru sor ve cevap bekle.",
    tone: "growth",
  },
  3: {
    id: "a3",
    digit: 3,
    name: "İlham",
    headline: "İfade eder, görünür kılar, neşe yayar.",
    strengths: "yaratıcılık, iletişim, akış",
    shadows: "dağınıklık, savrulma, yüzeysellik",
    practice: "15 dakika üret: yaz/çiz/konuş; sonuç değil akış önemli.",
    tone: "strength",
  },
  4: {
    id: "a4",
    digit: 4,
    name: "Kurucu",
    headline: "Sistem kurar, sağlamlaştırır, güven inşa eder.",
    strengths: "düzen, disiplin, sürdürülebilirlik",
    shadows: "katılık, eleştirellik, aşırı çalışma",
    practice: "Bugün 3 küçük adım yaz; sadece birini yapmak bile yeter.",
    tone: "practice",
  },
  5: {
    id: "a5",
    digit: 5,
    name: "Gezgin",
    headline: "Değiştirir, dönüştürür, yeni olana alan açar.",
    strengths: "esneklik, merak, hareket",
    shadows: "sabırsızlık, tutarsızlık, bağımlılıklar",
    practice: "Bir alışkanlığı %1 değiştir ve bedenine hareket ekle.",
    tone: "growth",
  },
  6: {
    id: "a6",
    digit: 6,
    name: "Şifacı",
    headline: "Bakım verir, sahiplenir, iyileştirici alan tutar.",
    strengths: "şefkat, sorumluluk, destek",
    shadows: "fazla yük alma, memnun etme, suçluluk",
    practice: "Sınır cümlesi: ‘Şu an buna evet diyemem’.",
    tone: "practice",
  },
  7: {
    id: "a7",
    digit: 7,
    name: "Arayıcı",
    headline: "Derinleşir, anlam arar, içgörü toplar.",
    strengths: "analiz, sezgisel akıl, ruhsallık",
    shadows: "mesafe, kuşku, içe kapanma",
    practice: "Tek soru: ‘Bu bana ne öğretiyor?’ ve bir cümle yaz.",
    tone: "growth",
  },
  8: {
    id: "a8",
    digit: 8,
    name: "Yönetici",
    headline: "Sonuç alır, kaynak yönetir, hedefe odaklanır.",
    strengths: "güç, strateji, bolluk bilinci",
    shadows: "sertlik, güç savaşı, hırs",
    practice: "Başarı kriterini yaz; adil paylaşım ve şeffaflık seç.",
    tone: "strength",
  },
  9: {
    id: "a9",
    digit: 9,
    name: "Bilge",
    headline: "Tamamlar, geniş perspektif kurar, anlamı büyütür.",
    strengths: "bilgelik, şefkat, kapsayıcılık",
    shadows: "aşırı fedakârlık, dağılma, vedalaşamama",
    practice: "Yarım kalan tek şeyi kapat; sadeleştir ve bırak.",
    tone: "practice",
  },
};

const ELEMENT_META: Record<ElementKey, { label: string; hint: string }> = {
  air: { label: "Hava", hint: "zihin, fikir, iletişim" },
  water: { label: "Su", hint: "duygu, sezgi, bağ" },
  fire: { label: "Ateş", hint: "enerji, cesaret, hareket" },
  earth: { label: "Toprak", hint: "yapı, güven, somut sonuç" },
  balance: { label: "Denge", hint: "kapsayıcılık, tamamlama, merkez" },
};

function safeDigit(n: number) {
  const v = Math.trunc(Number(n));
  if (!Number.isFinite(v)) return 1;
  if (v < 1) return 1;
  if (v > 9) return ((v - 1) % 9) + 1;
  return v;
}

function dominantElement(pin: number[]): ElementKey {
  const counts = countElements(pin);
  const entries = Object.entries(counts) as Array<[ElementKey, number]>;

  let best: [ElementKey, number] = ["balance", counts.balance ?? 0];
  for (const [key, value] of entries) {
    if (value > best[1]) best = [key, value];
  }

  return best[0];
}

export function computeArchetypeProfile(n: KarmaNumerology): KarmaArchetypeProfile {
  const primary = safeDigit(n.lifePathMaster ?? n.destinyNumber);
  const archetype = ARCHETYPES[primary] ?? ARCHETYPES[1];

  const element = dominantElement(n.pin);
  const meta = ELEMENT_META[element] ?? ELEMENT_META.balance;

  return {
    archetype,
    element,
    elementLabel: meta.label,
    elementHint: meta.hint,
  };
}

export function computeArchetype(n: KarmaNumerology) {
  return computeArchetypeProfile(n).archetype;
}

export function getArchetypeLabel(n: KarmaNumerology): string {
  const { archetype, elementLabel, elementHint } = computeArchetypeProfile(n);
  return `${archetype.name} (${elementLabel}: ${elementHint})`;
}
